import {
    API,
    graphqlOperation
} from "aws-amplify";
import {
    ListTeamMembersByTeamIdQuery,
    ListTeamMembersByTeamIdQueryVariables,
    ListTeamMembersByUserIdQuery,
    ListTeamMembersByUserIdQueryVariables,
    TeamMember
} from "../../../API";
import {
    listTeamMembersByTeamId,
    listTeamMembersByUserId
} from "../../../graphql/queries";

import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";
import { TeamMemberDAO } from "./TeamMemberDAO";

export class GraphQLTeamMemberDAO implements TeamMemberDAO {
    public static readonly LIST_BY_USER_ID_ATTEMPT = "GraphQLTeamMemberDAO.listByUserId.Attempt";
    public static readonly LIST_BY_USER_ID_FAILURE = "GraphQLTeamMemberDAO.listByUserId.Failure";
    public static readonly LIST_BY_TEAM_ID_ATTEMPT = "GraphQLTeamMemberDAO.listByTeamId.Attempt";
    public static readonly LIST_BY_TEAM_ID_FAILURE = "GraphQLTeamMemberDAO.listByTeamId.Failure";

    private readonly api: typeof API;
    private readonly gqlOperation: typeof graphqlOperation;
    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        api: typeof API,
        gqlOperation: typeof graphqlOperation,
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.api = api;
        this.gqlOperation = gqlOperation;
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async listByUserId(userId: string): Promise<TeamMember[]> {
        try {
            this.logger.info(`Getting team members for user ${userId}`, undefined, [GraphQLTeamMemberDAO.LIST_BY_USER_ID_ATTEMPT]);

            const variables: ListTeamMembersByUserIdQueryVariables = {
                userId: userId,
                filter: {
                    _deleted: {
                        ne: true
                    }
                }
            };

            const authToken: string = await this.idTokenSupplier.get();
            const gqlOperation = this.gqlOperation(
                listTeamMembersByUserId,
                variables,
                authToken
            );

            const queryResult = await this.api.graphql(gqlOperation) as GraphQLResult<ListTeamMembersByUserIdQuery>;
            return queryResult.data?.listTeamMembersByUserId?.items.filter((item): item is TeamMember => item !== null) ?? [];
        } catch (error) {
            this.logger.error(`Error getting team members for user ${userId}`, error, [GraphQLTeamMemberDAO.LIST_BY_USER_ID_FAILURE]);
            throw error;
        }
    }

    async listByTeamId(teamId: string): Promise<TeamMember[]> {
        try {
            this.logger.info(`Getting team members for team ${teamId}`, undefined, [GraphQLTeamMemberDAO.LIST_BY_TEAM_ID_ATTEMPT]);

            const variables: ListTeamMembersByTeamIdQueryVariables = {
                teamId: teamId,
                filter: {
                    _deleted: {
                        ne: true
                    }
                }
            };

            const authToken: string = await this.idTokenSupplier.get();
            const gqlOperation = this.gqlOperation(
                listTeamMembersByTeamId,
                variables,
                authToken
            );

            const queryResult = await this.api.graphql(gqlOperation) as GraphQLResult<ListTeamMembersByTeamIdQuery>;
            const items = queryResult.data?.listTeamMembersByTeamId?.items.filter((item): item is TeamMember => item !== null) ?? [];
            return items;
        } catch (error) {
            this.logger.error(`Error getting team members for team ${teamId}`, error, [GraphQLTeamMemberDAO.LIST_BY_TEAM_ID_FAILURE]);
            throw error;
        }
    }
}

export const graphQLTeamMemberDAO = new GraphQLTeamMemberDAO(
    API,
    graphqlOperation,
    ClientLoggerFactory.getClientLogger("GraphQLTeamMemberDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);